import {
  allAttemptsInVoucher,
  voucherIsFinished,
  voucherIsInProgress,
  voucherIsInReview,
} from '@/helpers/vouchers'
import {
  lastAttempt as certLastAttempt,
  certificationUserStatus,
  hasPassedAttempt,
} from '@/helpers/certifications'
import { debounce, throttle } from 'lodash'
import {
  differenceInDays,
  differenceInSeconds,
  format,
  formatDistanceToNow,
} from 'date-fns'
import { mapActions, mapGetters, mapState } from 'vuex'

import CONSTANTS from '@/constants'
import algoliasearch from 'algoliasearch'
import algoliasearchHelper from 'algoliasearch-helper'
import { eventsConstants } from './eventsList'
import { isBottomVisible } from '@/helpers'
import { stringDateToMilliseconds } from '@/helpers'

export const mixpanelEvent = {
  computed: {
    eventsConstants() {
      return eventsConstants
    },
  },
  methods: {
    sendMixpanelEvent(event, payload = {}) {
      if (this.$store.state.auth.isAuthenticated) {
        this.$store.dispatch('content/sendMixpanelEvent', {
          event_name: event,
          properties: {
            ...payload,
          },
        })
      }
    },
  },
}

export const localTimezone = {
  computed: {
    localTimeZone() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ')
    },
  },
}

export const bootcampCommons = {
  computed: {
    learningAreaName() {
      const {
        bootcamp: { learning_areas },
      } = this
      return learning_areas && learning_areas[0] && learning_areas[0].name
        ? learning_areas[0].name
        : 'IT Essentials'
    },
    lastInstructors() {
      const offset = this.instructorsOffset || 3
      const content = this.bootcamp || this.parent
      return content.instructors.length >= offset
        ? content.instructors
            .slice(offset, content.instructors.length)
            .map(item => {
              return item.name ? item.name : 'INE Instructor'
            })
            .join(', ')
        : ''
    },
    contentInstructors() {
      const offset = this.instructorsOffset || 3
      const content = this.bootcamp || this.parent
      if (content.instructors.length === 2) {
        return `${content.instructors[0].name} and ${content.instructors[1].name}`
      }
      const threeFirst = content.instructors
        .slice(0, offset)
        .map(item => {
          return item.name ? item.name : 'INE Instructor'
        })
        .join(', ')
      return `${threeFirst}${
        content.instructors.length > offset
          ? ` and ${content.instructors.length - offset} more`
          : ''
      }`
    },
    bootcampDate() {
      const {
        bootcamp: { start_datetime: start, end_datetime: end },
      } = this
      const startDate = new Date(start)
      const endDate = new Date(end)
      const sameMonth = format(endDate, 'MMMM') === format(startDate, 'MMMM')
      const toEndDate = sameMonth
        ? format(endDate, 'do')
        : format(endDate, 'MMMM do')
      return `
      ${format(startDate, 'MMMM do')} - ${toEndDate}`
    },
    userRegistered() {
      const { bootcamp } = this
      return bootcamp.user_status && bootcamp.user_status.registered
    },
    limitedInstructors() {
      const offset = this.instructorsOffset || 3
      return this.bootcamp.instructors && this.bootcamp.instructors.length
        ? this.bootcamp.instructors.slice(0, offset)
        : []
    },
  },
  methods: {
    getInstructorImg(instructor) {
      return instructor && instructor.profile_img_url
        ? instructor.profile_img_url
        : require('@/assets/images/instructor_fallback.png')
    },
    getInstructorName(instructor) {
      return instructor && instructor.name ? instructor.name : 'INE Instructor'
    },
  },
}

export const contentCommons = {
  methods: {
    isContentInParent(contentType, contentId, parentType, parent) {
      return parent?.content?.some(group => {
        if (
          parentType === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.PLAYLIST ||
          parentType === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.LEARNING_PATH
        ) {
          // parent structure for playlist and learnin-path
          // playlist.content -> groups
          // group.content -> items
          return group.content?.some(item => {
            return item.content_type === contentType && item.uuid === contentId
          })
        }
        // parent structure
        // course.content -> groups
        // group.content -> topics
        // topic.content -> items
        return group.content?.some(topic => {
          return topic.content?.some(item => {
            return item.content_type === contentType && item.uuid === contentId
          })
        })
      })
    },
  },
}

export const videoBookmarks = {
  computed: {
    ...mapGetters({
      courseBookmarks: 'content/courseBookmarks',
    }),
    videoBookmarks() {
      return this.courseBookmarks.reduce((acc, bookmark) => {
        if (bookmark.video === this.content.id)
          acc.push({
            begin: bookmark.time,
            cueType: 'bookmark',
            text: bookmark.notes,
          })
        return acc
      }, [])
    },
  },
  methods: {
    _addBookmarksClues() {
      // jwPlayer instance
      if (this.instance) this.instance.addCues(this.videoBookmarks)
    },
  },
  watch: {
    courseBookmarks() {
      this._addBookmarksClues()
    },
  },
}

export const windowResize = {
  provide() {
    return {
      getBreakpoint: () => this.breakpoint,
      getWindowWidth: () => this.windowWidth,
    }
  },
  data() {
    return {
      breakpoint: null,
      windowWidth: 0,
    }
  },
  computed: {
    isTablet() {
      return this.windowWidth <= 1024 && this.windowWidth > 768
    },
  },
  methods: {
    setBreakpoint(windowWidth) {
      if (windowWidth) {
        if (windowWidth >= 1800) this.breakpoint = 'lg-desktop'
        else if (windowWidth >= 1300) this.breakpoint = 'desktop'
        else if (windowWidth >= 1024) this.breakpoint = 'tablet'
        else if (windowWidth >= 900) this.breakpoint = 'medium-device'
        else if (windowWidth >= 768) this.breakpoint = 'mobile'
        else this.breakpoint = 'extra-small'

        this.windowWidth = windowWidth
      }
    },
  },
  mounted() {
    this.setBreakpoint(window.innerWidth)

    window.addEventListener('resize', () => {
      this.setBreakpoint(window.innerWidth)
    })
  },
  destroyed() {
    window.removeEventListener('resize', () => null)
  },
}

export const vouchersCommons = {
  data() {
    return {
      maxNumberOfExtensions: 3,
    }
  },
  computed: {
    activeVouchers() {
      return this?.voucher?.user_status?.active_vouchers ?? []
    },
    inactiveVouchers() {
      return this?.voucher?.user_status?.inactive_vouchers ?? []
    },
    allVouchers() {
      return [...this.activeVouchers, ...this.inactiveVouchers]
    },
    activeAttempts() {
      return this.allVouchers.reduce((acc, v) => {
        acc = [...acc, ...v.active_attempts]
        return acc
      }, [])
    },
    allAttempts() {
      return allAttemptsInVoucher(this.allVouchers)
    },
    isInBeta() {
      return this.voucher?.versions?.[0]?.is_beta
    },
    lastVoucherExpired() {
      if (this.lastVoucherExpirationDate) {
        return (
          differenceInSeconds(
            new Date().getTime(),
            this.lastVoucherExpirationDate.getTime()
          ) >= 0
        )
      }
      return false
    },
    lastVoucherExpirationDate() {
      return this.allVouchers.length
        ? new Date(this.allVouchers[0].expiration_date)
        : null
    },
    lastVoucherPrice() {
      return this.allVouchers.length ? this.allVouchers[0].price : null
    },
    lastVoucherId() {
      return this.allVouchers.length ? this.allVouchers[0].id : null
    },
    passingAttempt() {
      return this.allAttempts.find(a => hasPassedAttempt(a))
    },
    hasPassed() {
      return !!this.passingAttempt
    },
    isInProgress() {
      return voucherIsInProgress(this.allAttempts)
    },
    isFinished() {
      return voucherIsFinished(this.allAttempts)
    },
    isInReview() {
      return voucherIsInReview(this.allAttempts)
    },
    isEJPT() {
      return this.voucher.id === CONSTANTS.CERTIFICATIONS.IDS.eJPT
    },
    isExpired() {
      return (
        !this.activeVouchers.length &&
        !this.passingAttempt &&
        !this.isInReview &&
        this.lastVoucherExpired
      )
    },
    currentStatus() {
      if (this.isExpired) {
        return 'Expired'
      }
      if (this.hasPassed) {
        return 'Passed'
      }
      if (!this.activeVouchers?.length && !this.isInBeta && !this.isInReview) {
        return 'Shop'
      }
      if (!this.activeVouchers?.length && this.isInBeta && !this.isInReview) {
        return 'Beta'
      }
      if (this.isInProgress) {
        return 'In Progress'
      }
      if (this.isInReview) {
        return 'Pending Review'
      }
      if (
        this.activeVouchers?.length &&
        !this.activeAttempts.length &&
        !this.isFinished
      ) {
        return 'Start'
      }
      return 'Failed'
    },
    retriesAvailable() {
      if (this.activeVouchers?.length) {
        return this.activeVouchers.reduce((acc, voucher) => {
          acc += voucher.remaining_attempts
          return acc
        }, 0)
      }
      return 0
    },
    retryExpiry() {
      if (this.retriesAvailable > 0 && this.activeVouchers?.length) {
        if (
          this.activeVouchers?.length &&
          this.activeVouchers[0].next_attempt_expiration_date
        ) {
          return this.formatDate(
            this.activeVouchers[0].next_attempt_expiration_date
          )
        }
        return this.formatDate(this.activeVouchers[0].expiration_date)
      }
      return ''
    },
    learningArea() {
      return this?.voucher?.learning_area?.name ?? 'Networking'
    },
    learningPath() {
      return this.voucher?.learning_paths?.[0]
    },
    extensions() {
      return this.activeVouchers?.length
        ? this.activeVouchers[0].extensions
        : []
    },
    isExtendable() {
      return (
        !this.isInProgress &&
        !this.isInReview &&
        !this.isExpired &&
        this.currentStatus !== 'Failed' &&
        this.currentStatus !== 'Shop' &&
        this.extensions?.length < this.maxNumberOfExtensions
      )
    },
    reachedMaxExtensions() {
      return this.extensions.length >= this.maxNumberOfExtensions
    },
  },
  methods: {
    formatDate(date) {
      return format(new Date(date), 'LLLL d, yyyy')
    },
    updateVoucherExtensions(voucher) {
      const voucherToUpdate = this.allVouchers?.find(v => v.id === voucher.id)
      voucherToUpdate.extensions = voucher.extensions
      voucherToUpdate.expiration_date = voucher.expiration_date
      voucherToUpdate.next_attempt_expiration_date =
        voucher.next_attempt_expiration_date
    },
  },
}

export const certificationCommons = {
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
    }),
    ...mapGetters({
      activePlans: 'subscriptions/activePlans',
    }),
    userStatusCert() {
      return this.certification.awarded
        ? CONSTANTS.CERTIFICATIONS.USER_STATUS.AWARDED
        : certificationUserStatus(this.certification)
    },
    buttonText() {
      let text = null
      if (
        this.userStatusCert ===
        CONSTANTS.CERTIFICATIONS.USER_STATUS.ATTEMPT_IN_PROGRESS
      ) {
        text = 'Resume exam'
      } else if (
        this.userStatusCert ===
        CONSTANTS.CERTIFICATIONS.USER_STATUS.CAN_DO_RETAKE
      ) {
        text = 'Retake exam'
      } else if (
        this.userStatusCert === CONSTANTS.CERTIFICATIONS.USER_STATUS.AWARDED
      ) {
        text = 'Go to certification'
      } else if (
        this.userStatusCert === CONSTANTS.CERTIFICATIONS.USER_STATUS.NO_VOUCHERS
      ) {
        text = 'Shop vouchers'
      } else if (
        this.userStatusCert ===
        CONSTANTS.CERTIFICATIONS.USER_STATUS.READY_FOR_FIRST_ATTEMPT
      ) {
        text = 'Start exam'
      } else if (
        this.userStatusCert ===
        CONSTANTS.CERTIFICATIONS.USER_STATUS.NOT_AVAILABLE
      ) {
        text = 'Learn more'
      }
      return text
    },
    certlearningPath() {
      return this.certification.learning_paths?.length
        ? this.certification.learning_paths[0]
        : null
    },
    hasLearningPath() {
      return !!this.certlearningPath
    },
    lastAttempt() {
      return certLastAttempt(this.certification)
    },
    currentVersion() {
      return this.certification.versions?.[0]
    },
  },
  methods: {
    certificationRedirectURL(certId, isBeta = false) {
      const { PARAMS, REDIRECT_URL, BETA } =
        CONSTANTS.CERTIFICATIONS.SLUGS[certId] ||
        CONSTANTS.CERTIFICATIONS.SLUGS.DEFAULT
      if (isBeta) {
        return BETA ? `${BETA.REDIRECT_URL}${BETA.PATH}` : REDIRECT_URL
      }
      let slug = ''
      const product =
        this.isAuthenticated && this.activePlans?.length
          ? PARAMS?.VALUES?.VOUCHER
          : PARAMS?.VALUES?.PLAN || PARAMS?.VALUES?.VOUCHER
      if (product) slug = `${PARAMS.KEY}${product}`
      return `${REDIRECT_URL}${slug}`
    },
    getTrainingClick() {
      this.$router.push({
        name: 'learning-area-path',
        params: {
          area: this.certification.learning_area?.name?.replace(/\s/g, ''),
          id: this.certlearningPath?.id,
          slug: this.certlearningPath?.slug,
        },
      })
    },
  },
}
export const subscriptionsCommons = {
  data() {
    return {
      isPremiumUpgradeModalShown: false,
      isUpgradeModalShown: false,
      subscriptionIdForPlanUpgrade: '',
      couponCodeForPremium: null,
    }
  },
  computed: {
    ...mapState({
      validPlans: state => state.subscriptions.validPlans,
      isUpgradePlanAllowed: state => state.subscriptions.isUpgradePlanAllowed,
    }),
    ...mapGetters({
      usersSubscriptions: 'subscriptions/usersSubscriptions',
      currentActivePlans: 'subscriptions/activePlans',
    }),
    activePlans() {
      return this.currentActivePlans?.map(currentActivePlan => {
        const validPlans = this.validPlans.find(
          vp => vp.id === currentActivePlan.id
        )
        return { ...currentActivePlan, valid_plans: validPlans?.validPlans }
      })
    },
  },
  methods: {
    handlePlanUpgrade({ id, plan_code }) {
      this.subscriptionIdForPlanUpgrade = id
      this.isUpgradeModalShown = true
      const validCodes = this.validPlans.map(plan => plan.code)
      const premiumPlansCodes = Object.values(CONSTANTS.PLANS.PREMIUM_PLANS)
      if (
        validCodes &&
        validCodes.every(code => premiumPlansCodes.includes(code))
      ) {
        const coupon = new URLSearchParams(window.location.search).get('coupon')
        if (coupon && coupon.length) {
          this.couponCodeForPremium = coupon
        }
        this.isPremiumUpgradeModalShown = this.showOnlyPremiumPlans(plan_code)
      }
    },
    showOnlyPremiumPlans(plan_code) {
      return ![CONSTANTS.PLANS.MONTHLY, CONSTANTS.PLANS.ANNUAL].includes(
        plan_code
      )
    },
  },
}

export const licenseAddonCommons = {
  methods: {
    async requestAddonLicense(addon_code) {
      try {
        await this.$store.dispatch('business/requestLicenseAddon', addon_code)
        this.$store.dispatch(
          'app/setSuccess',
          "You've successfully requested an skill dive license"
        )
      } catch (error) {
        this.$store.dispatch('app/Error', error)
      }
    },
  },
}
export const labCommons = {
  computed: {
    ...mapState({
      currentLab: state => state.labs.currentLab,
    }),
    ...mapGetters({
      isOnTrialSubscription: 'subscriptions/isOnTrialSubscription',
      isCourtesyTrialUser: 'subscriptions/isCourtesyTrialUser',
    }),
    isWebAccess() {
      if (
        this.content &&
        this.content.content_type === CONSTANTS.CONTENT_TYPES.LAB
      ) {
        return this.content.is_web_access
      }
      return this.currentLab?.access_type === CONSTANTS.LABS.ACCESS_TYPES.WEB
    },
    labNotAvailable() {
      return (
        this.isOnTrialSubscription &&
        this.isWebAccess &&
        !this.isCourtesyTrialUser
      )
    },
  },
}

export const kinesisCommons = {
  computed: {
    ...mapState({
      currentCourse: state => state.courses.currentCourse,
      currentLearningPath: state => state.learningPaths.currentLearningPath,
      currentBootcamp: state => state.bootcamps.selectedBootcamp,
      currentParent: state => state.content.currentParent,
      businessUser: state => state.business.businessUser,
    }),
    parentData() {
      if (this.manualParent) {
        const parent_data = {}
        const parent_data_id = this.manualParent.objectID
          ? this.manualParent.objectID
          : this.manualParent.id
        parent_data[`${this.parentType}_id`] = parent_data_id
        parent_data[`${this.parentType}_slug`] = this.manualParent.slug
        if (this.parentType.instructors?.length) {
          const { instructors } = this.manualParent
          parent_data[`${this.parentType}_instructor_id`] =
            this.instructorId(instructors)
        }
        return {
          ...parent_data,
          parent_type: this.parentType,
          user_account_type: this.businessUser?.account?.id
            ? 'business'
            : 'customer',
          account_id: this.businessUser?.account?.id || null,
          account_name: this.businessUser?.account?.name || null,
          account_license_type:
            this.businessUser?.account?.license_type || null,
          account_pricing_plan:
            this.businessUser?.account?.pricing_plan || null,
          account_status: this.businessUser?.account?.status || null,
        }
      }

      let constructedParentData = {}
      if (
        this.parentType === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.LEARNING_PATH
      ) {
        const { id, slug, instructors } = this.currentLearningPath
        constructedParentData = {
          parent_id: id,
          parent_slug: slug,
          instructor_id: this.instructorId(instructors),
          parent_type: CONSTANTS.PARENT_CONTENT_TYPES_KEYS.LEARNING_PATH,
        }
      }
      if (this.parentType === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.PLAYLIST) {
        const { id, slug } = this.currentParent
        constructedParentData = {
          parent_id: id,
          parent_slug: slug,
          instructor_id: null,
          parent_type: CONSTANTS.PARENT_CONTENT_TYPES_KEYS.PLAYLIST,
        }
      }
      if (this.parentType === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.COURSE) {
        const { id, slug, instructors } = this.currentCourse
        constructedParentData = {
          parent_id: id,
          parent_slug: slug,
          instructor_id: this.instructorId(instructors),
          parent_type: CONSTANTS.PARENT_CONTENT_TYPES_KEYS.COURSE,
        }
      }

      constructedParentData = {
        ...constructedParentData,
        user_account_type: this.businessUser?.account?.id
          ? 'business'
          : 'customer',
        account_id: this.businessUser?.account?.id || null,
        account_name: this.businessUser?.account?.name || null,
        account_license_type: this.businessUser?.account?.license_type || null,
        account_pricing_plan: this.businessUser?.account?.pricing_plan || null,
        account_status: this.businessUser?.account?.status || null,
      }

      return { ...constructedParentData }
    },
  },
  methods: {
    instructorId(instructors) {
      return instructors && instructors[0] ? instructors[0].id : ''
    },
  },
}

export const collectionCommons = {
  data() {
    return {
      sortFunction: null,
      labs: [],
      sortingOptions: [
        { label: 'Recommended', facet: 'recomended' },
        { label: 'Newest', facet: 'published_date' },
        { label: 'Name (A - Z)', facet: 'name' },
      ],
    }
  },
  computed: {
    totalLabs() {
      return this.labs?.length || 0
    },
    difficulty() {
      return this.collection?.difficulty
    },
    collectionLearningAreaName() {
      return (
        this.collection?.learning_areas?.length &&
        this.collection?.learning_areas[0].name
      )
    },
    collectionLearningAreas() {
      return (
        this.collection?.learning_areas?.length &&
        this.collection?.learning_areas.map(la => la.name)
      )
    },
    description() {
      return this.collection?.description
    },
    formatedDateFromApi() {
      const { published_date } = this.content
      if (typeof published_date === 'string') {
        // comes string from content-api
        return new Date(published_date)
      } else {
        // comes number from algolia
        return new Date(published_date * 1000)
      }
    },
    addedTimeAgoLabel() {
      const days = differenceInDays(
        new Date(),
        new Date(this.formatedDateFromApi)
      )
      if (days >= 15 && days <= 30) {
        return 'Recently added'
      }
      const timeAgo = formatDistanceToNow(this.formatedDateFromApi, {
        addSuffix: true,
      })

      return `Added ${timeAgo}`
    },
    showAddedDaysAgo() {
      const { addedTimeAgoLabel } = this
      return addedTimeAgoLabel.match(/month|year/) ? false : addedTimeAgoLabel
    },
  },
  methods: {
    updateSort(order) {
      const facet = order?.facet
      switch (facet) {
        case 'published_date':
          this.sortFunction = (a, b) =>
            differenceInSeconds(
              stringDateToMilliseconds(a.published_date),
              stringDateToMilliseconds(b.published_date)
            )
          break
        case 'name':
          this.sortFunction = (a, b) =>
            a.name > b.name ? 1 : a.name < b.name ? -1 : 0
          break
        case 'recomended':
        default:
          this.sortFunction = null
      }

      this.sortingSelected = this.sortingOptions.findIndex(
        option => option.facet === order.facet
      )
    },
    allLabsFromCollection(collection) {
      let labsList = []
      collection?.content?.forEach(group =>
        group.content?.forEach(topic =>
          topic.content?.forEach(
            content => (labsList = labsList.concat(content))
          )
        )
      )
      this.labs = labsList
      return labsList
    },
  },
}

export const skillDiveLabsCommons = {
  data() {
    return {
      labContent: null,
      extraTag: null,
      extraTagTooltip: null,
      keyConceptList: [],
      skillDiveLabsCommonsLoading: false,
    }
  },
  computed: {
    ...mapGetters({
      userAvailableLearningArea: 'labExperience/userAvailableLearningArea',
      isSkillDiveB2CUser: 'auth/isSkillDiveB2CUser',
      isAccOwnerOrTeamManager: 'auth/isAccOwnerOrTeamManager',
      isBusinessUser: 'business/isBusinessUser',
    }),
    skillDiveDescription() {
      return this.labContent.description_html || this.labContent.description
    },
    lockedContent() {
      const las = this.labContent?.learning_areas.map(la => la.name)
      return !this.userAvailableLearningArea.some(la => las?.includes(la.name))
    },
  },
  methods: {
    setSkillDiveTags() {
      let tags = []
      const count = 6
      if (this.labContent?.tags?.length) {
        tags = this.labContent.tags.slice(0, count)
      }

      if (this.labContent?.tags?.length > count) {
        this.extraTag = `+ ${this.labContent?.tags?.length - count}`
        this.extraTagTooltip = this.labContent?.tags
          .slice(count - 1, this.labContent?.tags?.length)
          .join('\r\n')
      }

      this.keyConceptList = tags
    },
    async getAccessToLockedSDLab() {
      if (this.lockedContent) {
        if (this.isSkillDiveB2CUser) {
          this.$router.push({
            name: 'user-dashboard',
            params: {
              mode: 'subscriptions',
            },
          })
        } else if (this.isBusinessUser && !this.isAccOwnerOrTeamManager) {
          if (!this.skillDiveLabsCommonsLoading) {
            this.skillDiveLabsCommonsLoading = true
            await this.requestAddonLicense('skill-dive')
            this.skillDiveLabsCommonsLoading = false
          }
        } else {
          window.location.href = this.isAccOwnerOrTeamManager
            ? this.skillDiveEngage
            : this.checkoutUrl
        }
      }
    },
  },
}

const defaultSearchOptions = {
  triggerSearch: false,
  useUrl: false,
  useDebounceSearchQuery: false,
  hiddenFacets: [],
  hitsPerLoad: 12,
}
export const algoliaSearchMixin = {
  data() {
    return {
      algoliaClient: null,
      algoliaHelpers: {},
      searchOptions: {},
      debouncedSearchQuery: null,
      debouncedSearchQueryDelay: 500,
      throttleScroll: null,
      throttleScrollDelay: 100,
    }
  },
  computed: {
    ...mapState({
      algoliaKeys: state => state.search.algoliaKeys,
      stateQuery: state => state.search.searchQuery,
    }),
    searchQuery: {
      get() {
        return this.stateQuery
      },
      set(query) {
        this.setQuery(query)
      },
    },
  },
  beforeDestroy() {
    const { debouncedSearchQuery, throttleScroll } = this
    if (debouncedSearchQuery) debouncedSearchQuery.cancel()
    if (throttleScroll)
      window.removeEventListener('scroll', this.throttleScroll)
  },
  beforeRouteLeave(to, from, next) {
    next(vm => {
      if (from.name !== 'skill-dive') {
        vm.searchQuery = ''
      }
      next()
    })
  },
  watch: {
    stateQuery: {
      handler(query) {
        if (this.debouncedSearchQuery) {
          this.debouncedSearchQuery(query)
        }
      },
    },
  },
  created() {
    const { appKey, appId } = this.algoliaKeys

    this.algoliaClient = algoliasearch(appId, appKey)
  },
  methods: {
    ...mapActions({
      setQuery: 'search/setQuery',
    }),
    async initializeAlgoliaSearch(indexName, searchParameters, options = {}) {
      const client = this.algoliaClient
      const helper = algoliasearchHelper(client, indexName, searchParameters)

      // TODO: remove this line when we have a better solution
      helper.algoliaIndex = client.initIndex(indexName)

      this.$set(this.algoliaHelpers, indexName, helper)
      this.$set(this.searchOptions, indexName, {
        ...defaultSearchOptions,
        ...options,
      })

      helper.on('change', () => this.handleAlgoliaChange(indexName))
      helper.on('error', this.handleAlgoliaError)

      if (options.useUrl) {
        // At the moment it only supports one index at a time.
        this.handleUrlSearchQuery()
        this.handleUrlSearchParams(indexName)
      }

      if (options.useDebounceSearchQuery) {
        // At the moment it only supports one index at a time.
        this.debouncedSearchQuery = debounce(query => {
          this.handleAlgoliaSearchQuery(indexName, query)
        }, this.debouncedSearchQueryDelay)
      }
      if (options.triggerSearch) await helper.search()

      return helper
    },
    handleAlgoliaLazyLoad(indexName) {
      this.throttleScroll = throttle(() => {
        if (isBottomVisible()) {
          this.handleAlgoliaLoadMore(indexName)
        }
      }, this.throttleScrollDelay)
      window.addEventListener('scroll', this.throttleScroll)
    },
    handleAlgoliaSearchQuery(indexName, query) {
      const helper = this.algoliaHelpers[indexName]
      if (this.searchOptions[indexName].useUrl) this.updateUrlSearchQuery(query)
      helper.setQuery(query).search()
    },
    handleUrlSearchParams(indexName) {
      const params = new URLSearchParams(window.location.search)

      params.forEach((values, facet) => {
        if (!this.searchOptions[indexName].hiddenFacets.includes(facet)) {
          this.applyFilterByType(indexName, facet, values.split(','))
        }
      })
    },
    applyFilterByType(indexName, facet, values) {
      const helper = this.algoliaHelpers[indexName]
      const facetType = this.findOutRefinementType(indexName, facet)

      switch (facetType) {
        case 'and':
          values.forEach(value => {
            helper.addFacetRefinement(facet, value)
          })
          break
        case 'or':
          values.forEach(value => {
            helper.addDisjunctiveFacetRefinement(facet, value)
          })
          break
      }
    },
    findOutRefinementType(indexName, facet) {
      const helper = this.algoliaHelpers[indexName]

      if (helper.state?.facets?.includes(facet)) {
        return 'and'
      }
      if (helper.state?.disjunctiveFacets?.includes(facet)) {
        return 'or'
      }

      return null
    },
    encodeRefinements(indexName, refinements, params) {
      const { hiddenFacets } = this.searchOptions[indexName]

      Object.entries(refinements).forEach(([facet, values]) => {
        if (!hiddenFacets.includes(facet)) {
          if (values.length) {
            params.set(
              facet,
              values.map(value => encodeURIComponent(value)).join(',')
            )
          } else {
            params.delete(facet)
          }
        }
      })

      hiddenFacets.forEach(hf => params.delete(hf))
    },
    handleUrlSearchQuery() {
      const params = new URLSearchParams(window.location.search)
      this.searchQuery = params.get('search') || ''
    },
    updateUrlSearchParams(indexName) {
      const params = new URLSearchParams(window.location.search)
      const helper = this.algoliaHelpers[indexName]
      const { facetsRefinements, disjunctiveFacetsRefinements } = helper.state
      this.encodeRefinements(indexName, facetsRefinements, params)
      this.encodeRefinements(indexName, disjunctiveFacetsRefinements, params)

      window.history.replaceState(
        {},
        '',
        decodeURIComponent(`${window.location.pathname}?${params}`)
      )
    },
    updateUrlSearchQuery(query) {
      const params = new URLSearchParams(window.location.search)

      if (query) {
        params.set('search', query)
      } else {
        params.delete('search')
      }

      window.history.replaceState(
        {},
        '',
        decodeURIComponent(`${window.location.pathname}?${params}`)
      )
    },
    clearUrlParams() {
      const params = new URLSearchParams('')
      if (this.searchQuery) {
        params.set('search', this.searchQuery)
      }
      window.history.replaceState(
        {},
        '',
        decodeURIComponent(`${window.location.pathname}?${params}`)
      )
    },
    handleAlgoliaChange(indexName) {
      if (this.searchOptions[indexName].useUrl) {
        this.updateUrlSearchParams(indexName)
      }
    },
    handleAlgoliaError(error) {
      this.$store.dispatch('app/setError', error)
    },
    handleAlgoliaLoadMore(indexName) {
      const helper = this.algoliaHelpers[indexName]
      const nbHits = helper.lastResults?.nbHits || 0
      const { hitsPerPage } = helper.state
      const { hitsPerLoad } = this.searchOptions[indexName]

      if (hitsPerPage >= nbHits) return

      helper
        .setQueryParameter('hitsPerPage', hitsPerPage + hitsPerLoad)
        .search()
    },
    handleAlgoliaNextPage(indexName) {
      const helper = this.algoliaHelpers[indexName]

      helper.nextPage().search()
    },
    handleAlgoliaPreviousPage(indexName) {
      const helper = this.algoliaHelpers[indexName]

      helper.previousPage().search()
    },
    isAlgoliaFilterOrEnabled(indexName, { facet, value }) {
      const helper = this.algoliaHelpers[indexName]
      const disjunctiveFacetsRefinements =
        helper.state?.disjunctiveFacetsRefinements

      return disjunctiveFacetsRefinements[facet]?.includes(value)
    },
    toggleAlgoliaFilterAnd(indexName, filter) {
      const helper = this.algoliaHelpers[indexName]
      const { facet, value } = filter

      helper.toggleFacetRefinement(facet, value).search()
    },
    toggleAlgoliaFilterOr(indexName, filter) {
      const helper = this.algoliaHelpers[indexName]
      const { facet, value } = filter
      // toggleDisjunctiveFacetRefinement is not working properly, this is a workaround using add/remove
      if (this.isAlgoliaFilterOrEnabled(indexName, filter)) {
        helper.removeDisjunctiveFacetRefinement(facet, value)
      } else {
        helper.addDisjunctiveFacetRefinement(facet, value)
      }

      helper.search()
    },
    getIndexHits(indexName) {
      return this.algoliaHelpers[indexName]?.lastResults?.hits || []
    },
    clearAlgoliaRefinements(indexName) {
      const helper = this.algoliaHelpers[indexName]
      helper.clearRefinements()
      this.clearUrlParams()
    },
    setSearchParametersFilter(indexName, filters) {
      const helper = this.algoliaHelpers[indexName]
      helper.state.filters = filters
    },
  },
}

export const labHandlerCommons = {
  computed: {
    ...mapGetters({
      isChangingLabState: 'labs/isChangingLabState',
    }),
    isLabSessionRunning() {
      return (
        this.currentLabSession?.status === CONSTANTS.LABS.SESSION_STATUS.STARTED
      )
    },
  },
  methods: {
    ...mapActions('labs', [
      'getRunningLab',
      'updateLabStatus',
      'getLabSession',
      'getLabById',
      'partialUpdateLabSession',
      'createLabSession',
    ]),
    async updateSessionStatus(payload) {
      const { status, parentType, parentId } = payload
      const labSessionPayload = {
        labId: this.currentLab.id,
        sessionId: this.currentLabSession.id,
        status,
        parentId: this.parentId || parentId,
        parentType: this.parentType || parentType,
      }
      const response = await this.partialUpdateLabSession(labSessionPayload)
      if (response && response.errorCode) {
        this.$emit('custom-lab-error', response.errorCode)
      }
    },
    async toggleLab() {
      if (this.isLabSessionRunning) {
        await this.updateSessionStatus({
          status: CONSTANTS.LABS.SESSION_STATUS.TERMINATED,
        })
      }
    },
    async fetchContent() {
      const { labId, parentId, parentType } = this
      this.updateLabStatus({ isFetchingLab: true })
      await this.getRunningLab()
      const payload = {
        id: labId,
        parentId,
        parentType,
      }
      if (labId && parentId && parentType) await this.getLabById(payload)
      const sessionId = this.currentLab?.global_running_session?.id
      if (!this.isChangingLabState && sessionId) {
        const sessionPayload = {
          labId,
          parentId,
          parentType,
          sessionId,
        }
        await this.getLabSession(sessionPayload)
      }
      this.updateLabStatus({ isFetchingLab: false })
    },
    async switchLabs() {
      this.loadingText = this.isSwitchingLabs
        ? 'Switching labs'
        : 'Restarting lab'
      const regionSelected = localStorage.getItem(`pta-lab-region`) || 'US-East'
      const keyboardSelected =
        localStorage.getItem(`pta-lab-keyboard`) || 'en-us-qwerty'
      try {
        await this.toggleLab()
        await this.fetchContent()
        await this.createLabSession({
          id: this.currentLab.id,
          region: regionSelected,
          keyboard_layout: keyboardSelected,
          parentId: this.parentId,
          parentType: this.parentType,
        })
      } catch (e) {
        this.loadingText = ''
      } finally {
        this.updateLabStatus({
          isSwitchingLabs: false,
          isRestartingLab: false,
          isStartingLab: false,
        })
      }
    },
  },
}

export default mixpanelEvent
